/* Celebration alerts: once per real lodgement / approval / settlement, queued, with
   confetti and the company alert track (stored in the app). Local mute is per screen. */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Roll, dollars, money, pct0, reduceMotion } from './Ring';

const API = process.env.REACT_APP_BACKEND_URL;
const SEEN_KEY = 'gp_celebrated_keys';
const CEL = {
  lodged: { ac: '#0a84ff', tag: 'LODGEMENT' },
  approved: { ac: '#30d158', tag: 'APPROVED' },
  settled: { ac: '#64d2ff', tag: 'SETTLED' },
};
const localToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

/* ── Confetti ───────────────────────────────────────────────────────────── */
const Confetti = {
  cv: null, ctx: null, parts: [], on: false, last: 0,
  ensure() {
    if (this.cv) return;
    this.cv = document.createElement('canvas'); this.cv.id = 'gpj-confetti'; this.cv.setAttribute('aria-hidden', 'true');
    Object.assign(this.cv.style, { position: 'fixed', inset: 0, zIndex: 9996, pointerEvents: 'none', width: '100%', height: '100%' });
    document.body.appendChild(this.cv); this.ctx = this.cv.getContext('2d');
    const fit = () => { const d = Math.min(2, window.devicePixelRatio || 1); this.cv.width = window.innerWidth * d; this.cv.height = window.innerHeight * d; this.ctx.setTransform(d, 0, 0, d, 0, 0); };
    fit(); window.addEventListener('resize', fit);
  },
  burst(accent, side) {
    if (reduceMotion()) return;
    this.ensure();
    const W = window.innerWidth, H = window.innerHeight, cols = [accent, '#64d2ff', '#30d158', '#ffd60a', '#ffffff', '#bf5af2', '#0a84ff'];
    const add = (x, y, vx, vy) => this.parts.push({ x, y, vx, vy, s: 6 + Math.random() * 8, r: Math.random() * 6.28, vr: (Math.random() - 0.5) * 0.3, c: cols[Math.floor(Math.random() * cols.length)], sh: Math.random() < 0.7 ? 0 : 1, wob: Math.random() * 6.28, life: 0 });
    if (!side) for (let i = 0; i < 200; i++) add(Math.random() * W, -20 - Math.random() * H * 0.4, (Math.random() - 0.5) * 3, 2 + Math.random() * 3);
    for (let i = 0; i < 90; i++) { add(-10, H * 0.85, 6 + Math.random() * 9, -(10 + Math.random() * 9)); add(W + 10, H * 0.85, -(6 + Math.random() * 9), -(10 + Math.random() * 9)); }
    if (!this.on) { this.on = true; this.last = performance.now(); requestAnimationFrame((t) => this.step(t)); }
  },
  step(t) {
    const dt = Math.min(2.5, (t - this.last) / 16.7); this.last = t;
    const c = this.ctx, H = window.innerHeight; c.clearRect(0, 0, window.innerWidth, H);
    this.parts = this.parts.filter((p) => p.y < H + 30 && p.life < 900);
    for (const p of this.parts) {
      p.vy += 0.22 * dt; p.vx *= Math.pow(0.985, dt); p.vy = Math.min(p.vy, 5.5); p.wob += 0.08 * dt;
      p.x += (p.vx + Math.sin(p.wob) * 0.8) * dt; p.y += p.vy * dt; p.r += p.vr * dt; p.life += dt;
      c.save(); c.translate(p.x, p.y); c.rotate(p.r); c.fillStyle = p.c; c.globalAlpha = 0.95;
      if (p.sh) { c.beginPath(); c.arc(0, 0, p.s * 0.35, 0, 7); c.fill(); } else c.fillRect(-p.s / 2, -p.s / 4, p.s, p.s * 0.5 * Math.abs(Math.cos(p.wob)) + 1);
      c.restore();
    }
    if (this.parts.length) requestAnimationFrame((tt) => this.step(tt)); else { this.on = false; c.clearRect(0, 0, window.innerWidth, H); }
  },
};

/* ── Sound (company track from the API, local mute) ─────────────────────── */
function useAlertSound(token, settings) {
  const audio = useRef(null);
  const stopT = useRef(null);
  const fadeI = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [localMuted, setLocalMuted] = useState(() => localStorage.getItem('gp_alerts_muted') === '1');
  useEffect(() => { localStorage.setItem('gp_alerts_muted', localMuted ? '1' : '0'); }, [localMuted]);

  useEffect(() => {
    if (!token || !settings?.track_name) { setLoaded(false); return undefined; }
    let url;
    axios.get(`${API}/api/gameplan/alerts/track`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' })
      .then((r) => { url = URL.createObjectURL(r.data); const a = new Audio(url); a.preload = 'auto'; audio.current = a; setLoaded(true); })
      .catch(() => setLoaded(false));
    return () => { if (url) URL.revokeObjectURL(url); audio.current = null; };
  }, [token, settings?.track_name, settings?.track_version]);

  // Browsers need one user gesture before audio can play; prime it silently.
  useEffect(() => {
    const prime = () => { const a = audio.current; if (!a) return; a.muted = true; const p = a.play(); if (p?.then) p.then(() => { a.pause(); a.muted = false; setBlocked(false); }).catch(() => { a.muted = false; }); };
    document.addEventListener('pointerdown', prime, { once: true });
    return () => document.removeEventListener('pointerdown', prime);
  }, [loaded]);

  const span = useCallback(() => Math.max(1, (Number(settings?.end) || 15) - (Number(settings?.start) || 0)) * 1000, [settings]);
  const stop = useCallback(() => { clearTimeout(stopT.current); clearInterval(fadeI.current); audio.current?.pause(); }, []);
  const fadeOut = useCallback(() => {
    const a = audio.current; if (!a) return; const v0 = a.volume; let k = 0; clearInterval(fadeI.current);
    fadeI.current = setInterval(() => { k++; a.volume = Math.max(0, v0 * (1 - k / 9)); if (k >= 9) { clearInterval(fadeI.current); a.pause(); } }, 100);
  }, []);
  const play = useCallback(() => {
    const a = audio.current;
    if (!a || !settings?.on || localMuted) return false;
    stop();
    a.volume = Math.max(0, Math.min(1, Number(settings.vol ?? 0.9)));
    const seek = () => { try { a.currentTime = Number(settings.start) || 0; } catch (_) { /* not seekable yet */ } };
    if (a.readyState >= 1) seek(); else a.addEventListener('loadedmetadata', seek, { once: true });
    const p = a.play();
    if (p?.catch) p.catch(() => { setBlocked(true); toast.warning('Sound is waiting for a click', { description: 'Click anywhere on this screen once to allow alert sound' }); });
    stopT.current = setTimeout(fadeOut, Math.max(0, span() - 900));
    return true;
  }, [settings, localMuted, stop, fadeOut, span]);

  return { play, stop, span, loaded, blocked, localMuted, setLocalMuted, ready: loaded && !!settings?.on && !localMuted };
}

/* ── Context ────────────────────────────────────────────────────────────── */
const Ctx = createContext(null);
export const useCelebrations = () => useContext(Ctx);

export function CelebrationProvider({ token, children }) {
  const [settings, setSettings] = useState(null);
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const timer = useRef(null);
  const sound = useAlertSound(token, settings);

  const refreshSettings = useCallback(async () => {
    if (!token) return;
    try { const r = await axios.get(`${API}/api/gameplan/alerts/settings`, { headers: { Authorization: `Bearer ${token}` } }); setSettings(r.data); } catch (_) { /* keep last */ }
  }, [token]);
  useEffect(() => { refreshSettings(); }, [refreshSettings]);

  const enqueue = useCallback((item) => setQueue((q) => [...q, item].slice(-6)), []);
  const settingsRef = useRef(null);
  settingsRef.current = settings;

  // Celebrate real events that appeared since the last poll, once per deal+type, today only.
  const seen = useRef(null);
  const ingestFeed = useCallback((feed, totals) => {
    if (!Array.isArray(feed)) return;
    const keys = feed.map((e) => e.key);
    if (!seen.current) {
      const stored = new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]'));
      keys.forEach((k) => stored.add(k));
      seen.current = stored;
      localStorage.setItem(SEEN_KEY, JSON.stringify([...stored].slice(-600)));
      return;
    }
    const today = localToday();
    const fresh = feed.filter((e) => !seen.current.has(e.key) && e.at === today);
    keys.forEach((k) => seen.current.add(k));
    localStorage.setItem(SEEN_KEY, JSON.stringify([...seen.current].slice(-600)));
    fresh.reverse().forEach((e) => {
      const types = settingsRef.current?.types;
      if (types && types[e.type] === false) return;
      let line = '';
      if (e.type === 'lodged') line = totals?.lodgeTarget ? `Month now ${dollars(totals.lodged)} lodged · ${pct0(totals.lodged / totals.lodgeTarget)} of the ${money(totals.lodgeTarget, 0)} target` : `Month now ${dollars(totals?.lodged)} lodged`;
      else if (e.type === 'approved') line = `GCI ${dollars((e.amount || 0) * 0.0066)} · ${dollars(totals?.approved)} approved this month`;
      else line = totals?.settleTarget ? `${money(totals.settled)} settled this month · ${pct0(totals.settled / totals.settleTarget)} of ${money(totals.settleTarget, 0)}` : `${money(totals?.settled)} settled this month`;
      enqueue({ type: e.type, amount: e.amount, file: [e.client, e.lender, e.owner].filter(Boolean).join(' · '), line });
    });
  }, [enqueue]);

  const test = useCallback((type) => enqueue({ type, amount: 350000, file: 'Test alert · no figures have changed', line: 'Test alert. No figures have changed.', test: true }), [enqueue]);

  const dismiss = useCallback(() => {
    clearTimeout(timer.current); sound.stop(); setLeaving(true);
    setTimeout(() => { setCurrent(null); setLeaving(false); }, 550);
  }, [sound]);

  useEffect(() => {
    if (current || leaving || queue.length === 0) return;
    const [next, ...rest] = queue;
    setQueue(rest);
    const dur = sound.ready ? sound.span() : 8000;
    setCurrent({ ...next, dur });
    Confetti.burst(CEL[next.type].ac);
    setTimeout(() => Confetti.burst(CEL[next.type].ac, true), 2400);
    sound.play();
    timer.current = setTimeout(dismiss, dur);
  }, [queue, current, leaving, sound, dismiss]);

  const value = useMemo(() => ({ settings, refreshSettings, setSettings, ingestFeed, test, sound, current }), [settings, refreshSettings, ingestFeed, test, sound, current]);
  return (
    <Ctx.Provider value={value}>
      {children}
      {current && <CelebrationOverlay item={current} leaving={leaving} onDismiss={dismiss} />}
    </Ctx.Provider>
  );
}

function CelebrationOverlay({ item, leaving, onDismiss }) {
  const c = CEL[item.type];
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus({ preventScroll: true }); }, []);
  return (
    <div ref={ref} tabIndex={-1} role="alertdialog" aria-label={`${c.tag} ${dollars(item.amount)}`} className={`gpj gpj-celebrate${leaving ? ' out' : ''}`} style={{ '--ac': c.ac }}
      onClick={onDismiss} onKeyDown={(e) => { if (e.key === 'Escape' || e.key === 'Enter') onDismiss(); }} data-testid="gpj-celebration">
      <div className="gpj-cel-card">
        <span className="burst" /><span className="burst" />
        <span className="gpj-cel-tag"><i />{item.test ? 'TEST · ' : ''}{c.tag}</span>
        <div className="gpj-cel-amt" data-testid="gpj-celebration-amount"><Roll value={dollars(item.amount)} /></div>
        <div className="gpj-cel-file">{item.file}</div>
        <div className="gpj-cel-line">{item.line}</div>
        <div className="gpj-cel-time"><i style={{ animationDuration: `${item.dur}ms` }} /></div>
        <div className="gpj-cel-hint">Click anywhere to close</div>
      </div>
    </div>
  );
}
